/**
 * 클래스 상세 페이지 (오픈클래스/마이클래스 통합)
 * 경로: open-class/:id, class-management/:id, my-classes/:id, products/:id
 *
 * 정책:
 * 1) 액션: 참여권한 없음 → 온라인 주문, 참여권한 있음 → 세션 참여
 * 2) 클래스 현황 카드: 수강생 숨김, 어드민/마스터 표시 (마스터+오픈클래스는 해당 사용자가 참가자로 등록된 경우에만 표시)
 * 3) 커리큘럼: 공개 /open-class/:id 는 항상 전체 노출. 그 외 수강생·수강 중이면 학습한 시나리오만(강의 유지)·마이클래스 등에서 필터, 어드민은 전체
 * 4) 참가자 목록: 수강생 숨김, 어드민 전체·마스터는 소속 기관 참가자만
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Edit, Calendar, Globe, Building, CheckCircle, Copy, Award, Loader2, ShoppingCart, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
    PageHeader,
    BackToListLink,
    Button,
    Badge,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    Modal,
} from '@/components/shared/ui';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/shared/ui';
import ClassInviteModal from './ClassInviteModal';
import AddParticipantModal from './AddParticipantModal';
import CertificatePreviewModal from '@/components/class/CertificatePreviewModal';
import { type CurriculumItem } from '../../data/classes';
import { useAuth } from '../../contexts/AuthContext';
import ClassParticipantModal from './ClassParticipantModal';
import type { ClassCertificateData } from '@/lib/generateClassCertificatePdf';
import { useClassDetail as useClassDetailQuery, useClassUserList, useRemoveClassParticipant, classKeys } from '../../data/queries/useClasses';
import { getMyClassStatusMap, removeClassEnrollment } from '../../utils/myClassStatus';
import { formatCurrencyForLocale } from '@/lib/currencyFormat';
import {
    getLocalizedClassItem,
    isOpenClassItem,
    classDetailDtoToClassItem,
    getOpenClassListCoursePeriodLine,
    inferOpenClassListParticipationMode,
} from '@/utils/classUtils';
import { handleApiError } from '@/utils/errorHandlers';
import { addClassToCartIfNew } from '@/utils/cartStorage';
import { readTossCheckoutPending } from '@/lib/tossPendingCheckout';
import { ROUTES } from '@/lib/constants/routes';
import { getClassManagementListReturnQuery } from '@/lib/classManagementListQuery';
import { ApiError } from '@/services/apiClient';
import { resolvePublicFileUrlForImg } from '@/config/apiBaseUrl';
import { getScenarioBasicInfo } from '@/services/scenarioService';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import { practiceModeLabelFromScenarioInfo } from '@/lib/scenarioPracticeModeLabel';
import { formatLocaleDateShort } from '@/lib/localeFormat';

/** 커리큘럼 헤더 «총 N분» — durationMinutes 우선 */
function totalMinutesForCurriculum(items: CurriculumItem[]): number {
    const fromNumeric = items.reduce((acc, i) => acc + (i.durationMinutes ?? 0), 0);
    if (fromNumeric > 0) return fromNumeric;
    return items.reduce((acc, item) => {
        const match = item.duration?.match(/(\d+)/);
        return match ? acc + parseInt(match[1], 10) : acc;
    }, 0);
}

/** 수강 종료일(YYYY-MM-DD 또는 ISO 접두) — 당일 23:59:59.999까지는 미종료(UTC 자정 파싱으로 하루 빨리 종료되는 오류 방지) */
function isAttendanceEndedByEndYmd(endYmdOrIso: string | undefined): boolean {
    if (!endYmdOrIso) return false;
    const ymd = endYmdOrIso.slice(0, 10);
    const parts = ymd.split('-').map((x) => parseInt(x, 10));
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return false;
    const [y, m, d] = parts;
    const endLocal = new Date(y, m - 1, d, 23, 59, 59, 999);
    return Date.now() > endLocal.getTime();
}

/** 세션 소요시간 문자열을 언어별로 포맷 (예: "10분" → "10 min", "2시간 30분" → "2 hr 30 min") */
function formatDuration(
    duration: string | undefined,
    t: (key: string, opts?: Record<string, string | number>) => string,
    lang: string
): string {
    if (!duration || typeof duration !== 'string') return '—';
    const d = duration.trim();
    if (!d || d === '-') return '—';
    // "N시간 M분" or "N시간"
    const hm = d.match(/(\d+)\s*시간\s*(\d+)\s*분?/);
    if (hm) return `${hm[1]}${t('detail.hours')} ${hm[2]}${t('detail.minutes')}`;
    const hOnly = d.match(/(\d+)\s*시간/);
    if (hOnly) return `${hOnly[1]}${t('detail.hours')}`;
    // "N~M분"
    const range = d.match(/(\d+)\s*~\s*(\d+)\s*분/);
    if (range) return t('detail.durationRange', { min: range[1], max: range[2] });
    // "N분"
    const minOnly = d.match(/(\d+)\s*분/);
    if (minOnly) return `${minOnly[1]}${t('detail.minutes')}`;
    return d;
}

function formatDisplayDate(dateStr: string, lang: string): string {
    return dateStr ? formatLocaleDateShort(dateStr, lang) : '—';
}

export interface ClassDetailProps {
    classId?: string;
    onClose?: () => void;
}

export default function ClassDetail({ classId: propClassId, onClose }: ClassDetailProps = {}): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const apiLang = useApiLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { pathname } = location;
    const { id } = useParams<{ id: string }>();
    const { getCurrentRole, user } = useAuth();
    const role = getCurrentRole();
    const basePath = role === 'master' ? '/master' : role === 'admin' ? '/admin' : '';
    const queryClient = useQueryClient();

    const classId = propClassId || id || searchParams.get('tab') || '001';
    const numericClassId = parseInt(classId, 10) || undefined;
    // TODO: 용도에 맞는 API인지 확인 필요
    // 문제: 클래스 상세 페이지인데
    // 1단계: 클래스 상세 API (useClassDetailQuery)
    // 2단계: 클래스 사용자 목록 API (useClassUserList)
    // → 클래스 상세 하나로 충분해야 하는데 2개 API 조합
    const {
        data: classDetailDto,
        isLoading: loading,
        isError: isDetailQueryError,
        error: classDetailQueryError,
    } = useClassDetailQuery(numericClassId);
    const { data: classUserListDto } = useClassUserList(
        numericClassId,
        { page: 1, pageSize: 100 },
        { enabled: numericClassId != null && numericClassId > 0 && (role === 'admin' || role === 'master') }
    );
    const removeParticipantMutation = useRemoveClassParticipant();
    const classData = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    const isManagerPath = basePath.includes('master') || basePath.includes('admin');

    /** 기관 페이지 클래스 관리 섹션에서 진입 여부 확인 (?section=class-management) */
    const fromOrgClassManagement = searchParams.get('section') === 'class-management';
    /** embed 모드(onClose가 있으면) 또는 경로에 클래스 관리 포함 */
    const isMyClassPath = pathname.includes('my-classes') || 
                          pathname.includes('class-management') || 
                          fromOrgClassManagement ||
                          Boolean(onClose);
    /** App.tsx 공개 라우트 `/open-class/:id` — 학습 필터 없이 카탈로그용 전체 커리큘럼 */
    const isPublicOpenClassBrowseDetail = /^\/open-class\/[^/]+$/.test(pathname);
    /** 클래스 관리 목록에서 진입했는지 (쿼리 from=class-management) — 이때만 편집/복제·참가자평가관리 표시 */
    const fromClassManagement = searchParams.get('from') === 'class-management';

    const [isParticipantModalOpen, setIsParticipantModalOpen] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
    const [adminActiveTab, setAdminActiveTab] = useState<'curriculum' | 'participants'>('curriculum');
    const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
    const [selectedCertificateData, setSelectedCertificateData] = useState<ClassCertificateData | null>(null);

    const isEnrolled = useMemo(() => {
        // API가 isEnrolled를 내려주면 그것을 우선 사용
        if (classDetailDto?.isEnrolled !== undefined) {
            return classDetailDto.isEnrolled;
        }
        // fallback: localStorage 기반 판단
        const raw = localStorage.getItem('participatingClasses') || '{}';
        const participatingClasses = JSON.parse(raw) as Record<string, boolean>;
        const myStatus = getMyClassStatusMap()[classId];
        return !!(participatingClasses[classId] || myStatus);
    }, [classId, classDetailDto?.isEnrolled]);

    /**
     * 오픈클래스 주문 직후 백엔드가 수강 매핑까지 넣어 isEnrolled가 먼저 true가 될 수 있음.
     * 토스 결제가 아직 끝나지 않은 동안(sessionStorage pending)에는 구매·장바구니 CTA를 유지한다.
     */
    const showSessionJoinCta = useMemo(() => {
        const pendingIds = readTossCheckoutPending()?.selectedClassIds ?? [];
        const awaitingTossPayment = pendingIds.includes(classId);
        return isEnrolled && !awaitingTossPayment;
    }, [classId, isEnrolled]);

    /** 상세와 동일 로케일 커리큘럼 — 수강생 학습 필터에 사용 */
    const localizedCurriculum = useMemo(() => {
        if (!classDetailDto) return [] as CurriculumItem[];
        const item = classDetailDtoToClassItem(classDetailDto);
        return getLocalizedClassItem(item, i18n.language).curriculum ?? [];
    }, [classDetailDto, i18n.language]);

    /** 클래스 상세 classItemList에 mode가 없을 때 GET /scenario/{id}/info 로 보강 (스테일 캐시로 재요청 방지) */
    const [scenarioModeByScenarioId, setScenarioModeByScenarioId] = useState<Record<number, string>>({});
    const scenarioModeFetchIdRef = useRef(0);
    useEffect(() => {
        setScenarioModeByScenarioId({});
    }, [numericClassId]);

    const scenarioIdsToFetchForPracticeMode = useMemo(() => {
        const list = localizedCurriculum;
        const idSet = new Set(
            list
                .filter((c) => c.type === 'scenario' && typeof c.scenarioId === 'number' && c.scenarioId > 0)
                .map((c) => c.scenarioId as number)
        );
        return [...idSet].filter((id) => {
            const row = list.find((c) => c.scenarioId === id);
            if (!row) return false;
            if (String(row.practiceMode ?? '').trim()) return false;
            if (id in scenarioModeByScenarioId) return false; /* 있음(빈 문열=조회 끝, 라벨 없음) */
            return true;
        });
    }, [localizedCurriculum, scenarioModeByScenarioId]);

    useEffect(() => {
        if (scenarioIdsToFetchForPracticeMode.length === 0) return;
        const fetchGen = ++scenarioModeFetchIdRef.current;
        const classCtx = numericClassId != null && numericClassId > 0 ? String(numericClassId) : undefined;
        const ids = scenarioIdsToFetchForPracticeMode;
        void (async () => {
            const next: Record<number, string> = {};
            for (const id of ids) {
                if (fetchGen !== scenarioModeFetchIdRef.current) return;
                try {
                    const info = await getScenarioBasicInfo(id, apiLang, classCtx);
                    const label = practiceModeLabelFromScenarioInfo(info);
                    next[id] = label?.trim() ?? '';
                } catch {
                    next[id] = '';
                }
            }
            if (fetchGen !== scenarioModeFetchIdRef.current) return;
            setScenarioModeByScenarioId((prev) => ({ ...prev, ...next }));
        })();
    }, [scenarioIdsToFetchForPracticeMode, apiLang, numericClassId]);

    const localizedCurriculumWithScenarioModes = useMemo(() => {
        return localizedCurriculum.map((item) => {
            if (item.type !== 'scenario' || item.scenarioId == null) return item;
            const sid = item.scenarioId;
            if (String(item.practiceMode ?? '').trim()) return item;
            if (!(sid in scenarioModeByScenarioId)) return item;
            const fromInfo = scenarioModeByScenarioId[sid];
            if (!fromInfo?.trim()) return item; /* API에 라벨 없음/실패 */
            return { ...item, practiceMode: fromInfo };
        });
    }, [localizedCurriculum, scenarioModeByScenarioId]);

    /** 전체 커리큘럼 표시 */
    const curriculumForRoleView = localizedCurriculumWithScenarioModes;

    /** 오픈클래스·온라인 사이드바 수강기간: 대면과 동일 라벨·형식(조기 return 전에 계산 — 훅 순서 유지) */
    const onlineOpenClassSidebarPeriodText = useMemo(() => {
        if (!classData) return '';
        const dd = getLocalizedClassItem(classData, i18n.language);
        const pp = dd.participationPeriod;
        const inPerson = dd.educationVenue === 'inPerson' || !!(dd.educationSchedule);
        const openClass =
            isOpenClassItem(classData) || (role === 'master' && pathname.includes('/master/open-class/'));
        if (!openClass || inPerson) return '';
        if (pp?.startDate && pp?.endDate) {
            return `${formatDisplayDate(pp.startDate, i18n.language)} ~ ${formatDisplayDate(pp.endDate, i18n.language)}`;
        }
        const mode = inferOpenClassListParticipationMode(dd);
        const raw = getOpenClassListCoursePeriodLine(dd, mode, i18n.language);
        if (!raw?.trim()) return '—';
        const trimmed = raw.trim();
        if (/^\d+$/.test(trimmed)) {
            const n = parseInt(trimmed, 10);
            if (!Number.isFinite(n) || n < 1) return '—';
            return '—';
        }
        return raw;
    }, [classData, i18n.language, t, role, pathname]);

    const renderCurriculumTimeOrScore = useCallback(
        (item: CurriculumItem) => {
            if (item.type === 'video') return null;
            if (item.durationMinutes != null && item.durationMinutes > 0) {
                return (
                    <span className="text-sm text-gray-500 w-16 text-right shrink-0">
                        {item.durationMinutes}
                        {t('detail.minutes')}
                    </span>
                );
            }
            const fd = formatDuration(item.duration, t, i18n.language);
            return (
                <span className={`text-sm w-16 text-right shrink-0 ${fd === '—' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {fd}
                </span>
            );
        },
        [t, i18n.language]
    );

    useEffect(() => {
        const raw = localStorage.getItem('participatingClasses') || '{}';
        const participatingClasses = JSON.parse(raw) as Record<string, boolean>;
        if (classId === '001') {
            if (participatingClasses[classId]) {
                delete participatingClasses[classId];
                localStorage.setItem('participatingClasses', JSON.stringify(participatingClasses));
            }
        } else if (classId === '004') {
            // 오픈클래스 필수의료 시뮬레이션: 참가자로 등록되어 있지 않게 유지
            removeClassEnrollment('004');
        } else if (user != null && participatingClasses[classId] && !isManagerPath) {
            // 로그인한 경우에만 수강 중이면 마이클래스로 리다이렉트 (비로그인 시 오픈클래스 상세 유지)
            navigate(`/student/my-classes/${classId}`, { replace: true });
        }
    }, [classId, navigate, isManagerPath, user]);

    const masterOrgName = user?.currentAccount?.organizationName?.trim() ?? '';
    const currentUserOrgId = user?.currentAccount?.organizationId;
    
    /** 본인 기관의 클래스인지 확인 (기관관리자 권한 체크용) */
    const isOwnOrganizationClass = useMemo(() => {
        if (!currentUserOrgId || !classData?.organizationId) return false;
        // string과 number 비교를 위해 둘 다 number로 변환
        const userOrgId = Number(currentUserOrgId);
        const classOrgId = Number(classData.organizationId);
        return !isNaN(userOrgId) && !isNaN(classOrgId) && userOrgId === classOrgId;
    }, [currentUserOrgId, classData?.organizationId]);

    const adminParticipantList = useMemo(() => {
        if (role !== 'admin' && role !== 'master') return [];
        const rows = (classUserListDto?.userList ?? []).map((u) => ({
            name: u.realName,
            id: u.loginId,
            userId: String(u.userId),
            organizationName: u.organizationTitle ?? t('detail.defaultOrgName'),
            jobType: u.department ?? '—',
            position: u.position ?? '—',
            participantRole: 'USER',
        }));
        if (role === 'master' && masterOrgName) {
            return rows.filter((r) => (r.organizationName ?? '').trim() === masterOrgName);
        }
        return rows;
    }, [role, classUserListDto, t, masterOrgName]);

    /** 참가자 목록 (관리자·마스터, GET /class/{id}/user-list 기준) */
    const participantListDisplay = adminParticipantList;

    const jobTypeLabel = (job: string) => {
        const keyMap: Record<string, string> = {
            '의사': 'detail.jobDoctor',
            '간호사': 'detail.jobNurse',
            '구급대원': 'detail.jobParamedic',
        };
        const key = keyMap[job];
        return key ? t(key) : job;
    };

    const handleRemoveParticipant = async (participantUserId: string) => {
        if (!window.confirm(t('detail.confirmRemoveParticipant'))) return;
        if (!numericClassId || numericClassId <= 0) return;
        const uid = Number(participantUserId);
        if (!Number.isFinite(uid) || uid <= 0) return;
        try {
            await removeParticipantMutation.mutateAsync({ classId: numericClassId, userId: uid });
        } catch (err) {
            const { message } = handleApiError(err, t('detail.removeParticipantFailed'));
            window.alert(message);
        }
    };

    const handleRemoveAllParticipants = async () => {
        const removable = participantListDisplay.filter((row) => row.participantRole !== 'Master');
        const removableIds = removable.map((row) => row.userId);
        if (removableIds.length === 0) return;
        if (!window.confirm(t('detail.confirmRemoveAllParticipants', { count: removableIds.length }))) return;
        if (!numericClassId || numericClassId <= 0) return;
        try {
            for (const id of removableIds) {
                const uid = Number(id);
                if (!Number.isFinite(uid) || uid <= 0) continue;
                await removeParticipantMutation.mutateAsync({ classId: numericClassId, userId: uid });
            }
        } catch (err) {
            const { message } = handleApiError(err, t('detail.removeParticipantFailed'));
            window.alert(message);
        }
    };

    if (loading) {
        return (
            <div className="flex h-[400px] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
        );
    }

    if (isDetailQueryError || !classData) {
        const fallback = t('detail.classNotFound');
        /** 수강생 공개 오픈클래스 상세(/open-class/:id)만 — 어드민/마스터 동일 URL은 기관·관리 맥락으로 안내 */
        const isPublicOpenClassBrowseRoute =
            /^\/open-class\/[^/]+$/.test(pathname) && !isManagerPath;

        let message: string;
        if (isDetailQueryError && classDetailQueryError) {
            const err = classDetailQueryError;
            if (err instanceof ApiError && !isPublicOpenClassBrowseRoute) {
                if (err.status === 401) {
                    message = t('detail.classLoadAuthRequiredPrivate');
                } else if (err.status === 403) {
                    message = t('detail.classLoadForbiddenPrivate');
                } else {
                    message = handleApiError(err, fallback).message;
                }
            } else {
                message = handleApiError(err, fallback).message;
            }
        } else {
            message = fallback;
        }

        return (
            <div className="flex h-[400px] flex-col items-center justify-center gap-2 px-4 text-center text-gray-600">
                <p>{message}</p>
            </div>
        );
    }

    const displayData = getLocalizedClassItem(classData, i18n.language);
    const {
        title,
        thumbnail,
        description,
        participationPeriod,
        completionRequirements,
        maxParticipants = 0,
        currentParticipants = 0,
        completionRate = 0,
    } = displayData;
    /** DTO에 practiceMode가 없을 때 GET /scenario/{id}/info 로 합쳐진 커리큘럼 */
    const curriculum = localizedCurriculumWithScenarioModes;
    /** 탭(어드민/마스터)은 위 `curriculum` 전체. 일반 영역은 `curriculumForRoleView`(공개 오픈클래스는 전체, 마이클래스+수강 중은 학습 필터) */
    const curriculumMainList = curriculumForRoleView;
    const thumbnailSrc = resolvePublicFileUrlForImg(thumbnail) ?? thumbnail;

    // classUtils에서 isOnlineClass 여부를 educationVenue/onlinePlatform 힌트로 인코딩
    const isInPerson = displayData.educationVenue === 'inPerson' || !!(displayData.educationSchedule);
    const isExpired = participationPeriod && isAttendanceEndedByEndYmd(participationPeriod.endDate);
    const isCompleted = classDetailDto?.isCompleted === true;
    /** 어드민: 항상 표시. 마스터: 마이클래스(class-management)면 항상, 오픈클래스면 해당 사용자가 참가자로 등록된 경우에만 클래스 현황·참가자 목록 표시 */
    const showStatusAndParticipants = role === 'admin' || (role === 'master' && (isMyClassPath || isEnrolled));

    const handleDownloadCertificate = async () => {
        if (!classDetailDto || !user) return;
        
        try {
            const issueDate = new Date();
            const issueYear = issueDate.getFullYear();
            
            // 문서번호: 연도-classId-classUserMappingId
            const documentNumber = `${issueYear}-${classDetailDto.classId}-${classDetailDto.classUserMappingId || 0}`;
            
            // 사용자 정보
            const studentName = user.name || user.currentAccount?.displayName || user.currentAccount?.loginId || '—';
            const affiliationName = user.currentAccount?.organizationName || user.currentAccount?.affiliation || '—';
            
            // 클래스 정보
            const displayItem = classData ? getLocalizedClassItem(classData, i18n.language) : null;
            const courseName = displayItem?.title || classDetailDto.title || '—';
            
            // 교육기관: 파트너기관명 우선, 없으면 개설기관명
            const educationInstitutionName = classDetailDto.partnerOrganizationTitle || classDetailDto.organizationTitle || '—';
            
            // 수강기간: enrolledAt ~ expiresAt (또는 classEndDate, 또는 발급일 중 빠른 날짜)
            const formatDate = (dateStr: string | null | undefined) => {
                if (!dateStr) return null;
                const date = new Date(dateStr);
                return date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '');
            };
            
            // 수강 시작일: enrolledAt 우선, 없으면 classStartDate 사용
            const enrolledAtFormatted = formatDate(classDetailDto.enrolledAt as string | null) 
                || formatDate(classDetailDto.classStartDate as string | null);
            
            console.log('이수증 날짜 데이터:', {
                enrolledAt: classDetailDto.enrolledAt,
                expiresAt: classDetailDto.expiresAt,
                classStartDate: classDetailDto.classStartDate,
                classEndDate: classDetailDto.classEndDate,
                enrolledAtFormatted
            });
            
            // 수강 종료일 결정 로직:
            // 1. expiresAt(수강 만료일)이 있으면 사용
            // 2. 없으면 classEndDate(클래스 종료일) 사용
            // 3. 둘 다 없으면 발급일(issueDate) 사용
            // 4. 선택된 날짜가 발급일보다 미래면 → 발급일로 변경 (이수증은 과거 기간만 표시)
            const expiresAtDate = classDetailDto.expiresAt ? new Date(classDetailDto.expiresAt as string) : null;
            const classEndDateDate = classDetailDto.classEndDate ? new Date(classDetailDto.classEndDate as string) : null;
            
            let periodEndDate: Date;
            if (expiresAtDate) {
                // 수강 만료일이 있으면 사용 (단, 발급일보다 미래면 발급일)
                periodEndDate = expiresAtDate > issueDate ? issueDate : expiresAtDate;
            } else if (classEndDateDate) {
                // 클래스 종료일이 있으면 사용 (단, 발급일보다 미래면 발급일)
                periodEndDate = classEndDateDate > issueDate ? issueDate : classEndDateDate;
            } else {
                // 둘 다 없으면 발급일 사용
                periodEndDate = issueDate;
            }
            
            const periodEndFormatted = formatDate(periodEndDate.toISOString());
            
            // 수강기간 텍스트 생성
            let participationPeriodText: string;
            if (enrolledAtFormatted && periodEndFormatted) {
                // 시작일과 종료일 모두 있음
                participationPeriodText = `${enrolledAtFormatted} ~ ${periodEndFormatted}`;
            } else if (periodEndFormatted) {
                // 종료일만 있음 (시작일 없음)
                participationPeriodText = `~ ${periodEndFormatted}`;
            } else {
                // 둘 다 없음 (이 경우는 발생하지 않아야 함)
                participationPeriodText = '—';
            }
            
            console.log('이수증 기간 포맷:', {
                periodEndDate,
                periodEndFormatted,
                participationPeriod: participationPeriodText
            });
            
            const issueDateText = issueDate.toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
            
            // 개설 기관 로고 URL (API 응답에서 직접 가져오기)
            const issuerLogoImageUrl = classDetailDto.organizationLogoImageUrl || null;
            
            const certificateData: ClassCertificateData = {
                documentNumber,
                studentName,
                affiliationName,
                courseName,
                educationInstitutionName,
                participationPeriodText,
                issueDateText,
                issuerSignatureImageUrl: null, // API 미제공
                issuerLogoImageUrl,
                partnerLogoImageUrl: null,
                labels: {
                    title: t('certificate.title'),
                    affiliation: t('certificate.affiliation'),
                    studentName: t('certificate.studentName'),
                    courseName: t('certificate.courseName'),
                    educationInstitution: t('certificate.educationInstitution'),
                    period: t('certificate.period'),
                    issueDate: t('certificate.issueDate'),
                },
            };
            
            setSelectedCertificateData(certificateData);
            setIsCertificateModalOpen(true);
        } catch (error) {
            console.error('이수증 데이터 생성 실패:', error);
            alert('이수증 생성에 실패했습니다.');
        }
    };

    // 어드민: 클래스 관리 > 클래스명 / 그 외: 오픈클래스 상세면 오픈클래스 > 클래스명, 마이클래스 상세면 마이클래스 > 클래스명
    const breadcrumbLabel = role === 'admin' ? t('detail.breadcrumbClassManagement') : isMyClassPath ? t('detail.breadcrumbMyClass') : t('detail.breadcrumbOpenClass');
    const breadcrumbLinkBase =
        role === 'admin'
            ? '/admin/class-management'
            : isMyClassPath
                ? role === 'master'
                    ? '/master/class-management'
                    : '/student/my-classes'
                : ROUTES.COMMON.CLASS_LIST;

    const listReturnQuery = role === 'admin' || role === 'master' ? getClassManagementListReturnQuery(basePath, location.state) : '';
    const withListReturn = (p: string) => {
        if (!listReturnQuery) return p;
        if (p === '/admin/class-management' || p === '/master/class-management') {
            return `${p}${listReturnQuery}`;
        }
        return p;
    };
    const breadcrumbLink = withListReturn(breadcrumbLinkBase);

    /** 목록으로 링크: 공개 오픈클래스 상세는 항상 일반 목록으로, 클래스관리에서 왔으면 클래스관리 목록, 아니면 역할별 마이클래스 등 */
    const isPublicOpenClassDetail = pathname.startsWith('/open-class/') && !pathname.startsWith('/admin');
    const backToListLink = withListReturn(
        isPublicOpenClassDetail
            ? ROUTES.COMMON.CLASS_LIST
            : pathname.includes('my-classes')
                ? fromClassManagement
                    ? (role === 'admin' ? '/admin/class-management' : role === 'master' ? '/master/class-management' : '/student/my-classes')
                    : (role === 'admin' ? '/admin/class-management' : role === 'master' ? '/master/my-classes' : '/student/my-classes')
                : breadcrumbLinkBase
    );

    /** 오픈클래스/마이클래스/클래스관리 상세: 동일한 레이아웃(좌측 정렬, 배경 없음, 목록으로 링크) */
    const isSimpleDetailLayout = pathname.includes('open-class') || pathname.includes('my-classes') || pathname.includes('class-management');
    /** 오픈클래스: classType·경로 기준 */
    const isOpenClass =
        isOpenClassItem(classData) ||
        (role === 'admin' && pathname.includes('/admin/open-class/')) ||
        (role === 'master' && pathname.includes('/master/open-class/'));
    /** 오픈클래스·온라인 참여: 정원 제한 없음 — 클래스 현황 카드는 현재 참여자 수만 표시 */
    const isOnlineOpenClass = isOpenClass && !isInPerson;
    const isFull = !isOnlineOpenClass && currentParticipants >= maxParticipants;
    /** 클래스 변경/복제 버튼: 클래스 관리에서 진입한 경우(어드민/마스터) — 마스터+오픈클래스 제외 */
    const showClassEditActions =
        fromClassManagement && (role === 'admin' || role === 'master') && !(role === 'master' && isOpenClass);
    /** 마스터+오픈클래스: 세션 관리 숨김 (단, 마이클래스 경로에서는 표시) */
    const showSessionManageForMaster = !(role === 'master' && isOpenClass) || (role === 'master' && isMyClassPath);
    /** 마스터+오픈클래스: 초대하기 숨김 (단, 마이클래스 경로에서는 표시). 수강 종료일 지나면 비노출 */
    const showInviteButton =
        !isExpired &&
        !isOpenClass;

    return (
        <div className={isSimpleDetailLayout ? 'max-w-[1280px] mx-auto px-4 py-8' : 'max-w-[1460px] mx-auto pb-12'}>
            {isSimpleDetailLayout ? (
                <>
                    <BackToListLink 
                        to={onClose ? undefined : backToListLink}
                        onClick={onClose}
                        className="mb-4"
                    >
                        {t('detail.backToList')}
                    </BackToListLink>
                    <PageHeader
                        title={title}
                        actions={
                            isExpired ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gray-500 text-white">
                                    {t('detail.ended')}
                                </span>
                            ) : isMyClassPath && isCompleted ? (
                                <Button
                                    variant="outline"
                                    className="border-brand-500 text-brand-600 inline-flex items-center gap-2"
                                    onClick={handleDownloadCertificate}
                                >
                                    <Award size={16} />
                                    {t('certificate.issueCertificate')}
                                </Button>
                            ) : undefined
                        }
                    />
                </>
            ) : (
                <>
                    <BackToListLink 
                        to={onClose ? undefined : backToListLink}
                        onClick={onClose}
                        className="mb-4"
                    >
                        {t('detail.backToList')}
                    </BackToListLink>
                    <PageHeader
                        title={title}
                        align="left"
                        withBackground
                        breadcrumbs={[
                            { label: breadcrumbLabel, link: breadcrumbLink },
                            { label: title }
                        ]}
                        actions={
                            isExpired ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gray-500 text-white">
                                    {t('detail.ended')}
                                </span>
                            ) : isMyClassPath && isCompleted ? (
                                <Button
                                    variant="outline"
                                    className="border-brand-500 text-brand-600 inline-flex items-center gap-2"
                                    onClick={handleDownloadCertificate}
                                >
                                    <Award size={16} />
                                    {t('certificate.issueCertificate')}
                                </Button>
                            ) : undefined
                        }
                    />
                </>
            )}

            <div className={`flex flex-col lg:flex-row gap-8 ${isSimpleDetailLayout ? 'mt-6' : ''}`}>
                <div className="flex-1 space-y-6">
                    <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden border border-gray-200">
                        {thumbnailSrc ? (
                            <img src={thumbnailSrc} alt={title} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <span>{t('detail.noThumbnail')}</span>
                            </div>
                        )}
                    </div>

                    {description != null && String(description).trim() !== '' && (
                        <div
                            className={`text-gray-600 text-sm leading-relaxed whitespace-pre-wrap ${
                                isSimpleDetailLayout ? 'max-w-3xl' : 'max-w-3xl mx-auto text-center'
                            }`}
                        >
                            {description}
                        </div>
                    )}

                    {showStatusAndParticipants ? (
                        <Tabs value={adminActiveTab} onValueChange={(v) => setAdminActiveTab(v as 'curriculum' | 'participants')} className="w-full">
                            <TabsList className="w-full grid grid-cols-2 rounded-lg bg-gray-100 p-1">
                                <TabsTrigger value="curriculum" className="rounded-md">{t('detail.tabCurriculum')}</TabsTrigger>
                                <TabsTrigger value="participants" className="rounded-md">{t('detail.tabParticipants')}</TabsTrigger>
                            </TabsList>
                            <TabsContent value="curriculum" className="mt-4">
                                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                                    <div className="px-2 py-4 border-b border-gray-200 flex justify-between items-center">
                                        <span className="text-sm font-semibold text-gray-700">
                                            {t('detail.sessionsCountLabel')} {curriculum.length}
                                            <span className="mx-2">/ {t('detail.estimatedDuration')} </span>
                                            {(() => {
                                                const totalMinutes = totalMinutesForCurriculum(curriculum);
                                                const hours = Math.floor(totalMinutes / 60);
                                                const minutes = totalMinutes % 60;
                                                if (hours > 0) {
                                                    return minutes > 0
                                                        ? `${hours}${t('detail.hours')} ${minutes}${t('detail.minutes')}`
                                                        : `${hours}${t('detail.hours')}`;
                                                }
                                                return `${totalMinutes}${t('detail.minutes')}`;
                                            })()}
                                        </span>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {curriculum.map((item, index) => (
                                            <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                                                {/* 데스크탑: 가로 배치 */}
                                                <div className="hidden md:flex items-center gap-2">
                                                    <span className="text-gray-500 text-sm font-medium w-10 text-center">{index + 1}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start gap-3">
                                                            <Badge
                                                                variant="outline"
                                                                className={`shrink-0 text-sm w-24 justify-center ${item.type === 'video'
                                                                    ? 'text-green-500 border-green-500'
                                                                    : 'text-blue-500 border-blue-500'
                                                                    }`}
                                                            >
                                                                {item.type === 'video' ? t('detail.video') : t('detail.simulation')}
                                                            </Badge>
                                                            <div className="min-w-0 flex-1">
                                                                <h4 className="text-sm font-medium text-gray-900 break-words">
                                                                    {item.name}
                                                                </h4>
                                                                {item.practiceMode && (
                                                                    <p className="text-xs text-gray-500 mt-0.5 break-words">
                                                                        {item.practiceMode}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        {renderCurriculumTimeOrScore(item)}
                                                    </div>
                                                </div>

                                                {/* 모바일: 세로 배치 */}
                                                <div className="md:hidden space-y-3">
                                                    {/* 순번 + 뱃지 */}
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-500 text-sm font-medium">{index + 1}</span>
                                                        <Badge
                                                            variant="outline"
                                                            className={`text-xs px-2 py-0.5 ${item.type === 'video'
                                                                ? 'text-green-500 border-green-500'
                                                                : 'text-blue-500 border-blue-500'
                                                                }`}
                                                        >
                                                            {item.type === 'video' ? t('detail.video') : t('detail.simulation')}
                                                        </Badge>
                                                    </div>
                                                    {/* 세션명 */}
                                                    <h4 className="text-sm font-medium text-gray-900">
                                                        {item.name}
                                                    </h4>
                                                    {/* 실습모드 + 소요시간 */}
                                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                                        <span>{item.practiceMode || '—'}</span>
                                                        {renderCurriculumTimeOrScore(item)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value="participants" className="mt-4">
                                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                                    <div className="px-2 py-4 border-b border-gray-200 flex justify-between items-center">
                                        <span className="text-sm font-semibold text-gray-700">
                                            {t('detail.participantsTotal', { count: participantListDisplay.length })}
                                        </span>
                                        {participantListDisplay.some((row) => row.participantRole !== 'Master') && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => void handleRemoveAllParticipants()}
                                                disabled={removeParticipantMutation.isPending}
                                                icon={<Trash2 size={14} />}
                                            >
                                                {t('detail.removeAll')}
                                            </Button>
                                        )}
                                    </div>
                                    <Table className="table-fixed">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="px-2 py-3">{t('detail.colName')}</TableHead>
                                                <TableHead className="px-2 py-3">{t('detail.colId')}</TableHead>
                                                <TableHead className="px-2 py-3">{t('detail.colOrganization')}</TableHead>
                                                <TableHead className="px-2 py-3">{t('detail.colJob')}</TableHead>
                                                <TableHead className="px-2 py-3">{t('detail.colPosition')}</TableHead>
                                                <TableHead className="px-2 py-3 w-12 text-center">{t('detail.colRemove')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {participantListDisplay.map((row, idx) => (
                                                <TableRow key={row.id}>
                                                    <TableCell className="px-2 py-2 font-medium text-gray-900">{row.name}</TableCell>
                                                    <TableCell className="px-2 py-2 text-gray-600">{row.id}</TableCell>
                                                    <TableCell className="px-2 py-2 text-gray-600">{row.organizationName}</TableCell>
                                                    <TableCell className="px-2 py-2 text-gray-600">{jobTypeLabel(row.jobType)}</TableCell>
                                                    <TableCell className="px-2 py-2 text-gray-600">{row.position}</TableCell>
                                                    <TableCell className="px-2 py-2 text-center w-12">
                                                        <div className="flex justify-center items-center">
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-600 hover:bg-gray-100 transition-colors focus:ring-1 focus:ring-offset-1 focus:ring-red-400 disabled:opacity-40 disabled:pointer-events-none"
                                                                onClick={() => void handleRemoveParticipant(row.userId)}
                                                                disabled={removeParticipantMutation.isPending}
                                                                aria-label={t('detail.removeParticipantAria')}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {participantListDisplay.length === 0 && (
                                        <div className="p-8 text-center text-gray-500 text-sm">{t('detail.noParticipants')}</div>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    ) : (
                        <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                                <h3 className="font-bold text-gray-900">{t('detail.curriculumTitle')}</h3>
                                <span className="text-sm text-gray-700">
                                    {t('detail.sessionsCountTotal', { count: curriculumMainList.length })}
                                    <span className="mx-2">/</span>
                                    {(() => {
                                        const totalMinutes = totalMinutesForCurriculum(curriculumMainList);
                                        const hours = Math.floor(totalMinutes / 60);
                                        const minutes = totalMinutes % 60;
                                        if (hours > 0) {
                                            return minutes > 0
                                                ? `${hours}${t('detail.hours')} ${minutes}${t('detail.minutes')}`
                                                : `${hours}${t('detail.hours')}`;
                                        }
                                        return `${totalMinutes}${t('detail.minutes')}`;
                                    })()}
                                </span>
                            </div>
                            {role === 'student' && isEnrolled && curriculumMainList.length === 0 && curriculum.length > 0 && (
                                <div className="px-6 py-4 text-sm text-gray-600 bg-amber-50 border-b border-amber-100">
                                    {t('detail.curriculumLearnedEmpty')}
                                </div>
                            )}
                            <div className="divide-y divide-gray-100">
                                {curriculumMainList.map((item, index) => (
                                    <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                                        {/* 데스크탑: 가로 배치 */}
                                        <div className="hidden md:flex items-center gap-2">
                                            <span className="text-gray-500 text-sm font-medium w-10 text-center">{index + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start gap-3">
                                                    <Badge
                                                        variant="outline"
                                                        className={`shrink-0 text-sm w-24 justify-center ${item.type === 'video'
                                                            ? 'text-green-500 border-green-500'
                                                            : 'text-blue-500 border-blue-500'
                                                            }`}
                                                    >
                                                        {item.type === 'video' ? t('detail.video') : t('detail.simulation')}
                                                    </Badge>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-sm font-medium text-gray-900 break-words">
                                                            {item.name}
                                                        </h4>
                                                        {item.practiceMode && (
                                                            <p className="text-xs text-gray-500 mt-0.5 break-words">
                                                                {item.practiceMode}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                {renderCurriculumTimeOrScore(item)}
                                            </div>
                                        </div>

                                        {/* 모바일: 세로 배치 */}
                                        <div className="md:hidden space-y-3">
                                            {/* 순번 + 뱃지 */}
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-500 text-sm font-medium">{index + 1}</span>
                                                <Badge
                                                    variant="outline"
                                                    className={`text-xs px-2 py-0.5 ${item.type === 'video'
                                                        ? 'text-green-500 border-green-500'
                                                        : 'text-blue-500 border-blue-500'
                                                        }`}
                                                >
                                                    {item.type === 'video' ? t('detail.video') : t('detail.simulation')}
                                                </Badge>
                                            </div>
                                            {/* 세션명 */}
                                            <h4 className="text-sm font-medium text-gray-900">
                                                {item.name}
                                            </h4>
                                            {/* 실습모드 + 소요시간 */}
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>{item.practiceMode || '—'}</span>
                                                {renderCurriculumTimeOrScore(item)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="w-full lg:w-1/3 space-y-6">
                    <div className="bg-white rounded-xl border border-blue-400 p-6 shadow-sm space-y-4">
                        {(displayData.organizationLogo || displayData.organizationName) && (
                            <div className="flex items-center justify-between text-lg font-bold text-gray-900">
                                {displayData.organizationLogo ? (
                                    <img
                                        src={displayData.organizationLogo}
                                        alt={displayData.organizationName || t('organizationLogoAlt')}
                                        className="h-6 object-contain"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            const parent = e.currentTarget.parentElement;
                                            if (parent && displayData.organizationName) {
                                                const text = document.createElement('div');
                                                text.className = 'text-sm text-gray-600 font-medium';
                                                text.textContent = displayData.organizationName;
                                                parent.appendChild(text);
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="text-xl font-bold text-blue-500 py-3">
                                        {displayData.organizationName}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="space-y-3">
                            {isInPerson ? (
                                <>
                                    {isOpenClass && (
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                                <Building size={16} className="text-gray-400" />
                                                {t('openListParticipationMethod')}
                                            </span>
                                            <span className="font-medium text-gray-900 text-sm text-right">
                                                {t('inPersonParticipation')}
                                            </span>
                                        </div>
                                    )}
                                    {!isMyClassPath && (
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                                <Users size={16} className="text-gray-400" />
                                                {t('detail.recruitmentCount')}
                                            </span>
                                            <span className="font-medium text-gray-900 text-sm">
                                                {currentParticipants} / {maxParticipants}{t('detail.peopleUnit')}
                                            </span>
                                        </div>
                                    )}
                                    {!isMyClassPath && displayData.recruitmentPeriod && (
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                                <Calendar size={16} className="text-gray-400" />
                                                {t('recruitmentPeriod')}
                                            </span>
                                            <span className="font-medium text-gray-900 text-sm text-right">
                                                {formatDisplayDate(displayData.recruitmentPeriod.startDate ?? '', i18n.language)} ~ {formatDisplayDate(displayData.recruitmentPeriod.endDate ?? '', i18n.language)}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                            <Calendar size={16} className="text-gray-400" />
                                            {t('detail.participationPeriod')}
                                        </span>
                                        <span className="font-medium text-gray-900 text-sm text-right">
                                            {participationPeriod
                                                ? `${formatDisplayDate(participationPeriod.startDate, i18n.language)} ~ ${formatDisplayDate(participationPeriod.endDate, i18n.language)}`
                                                : '—'}
                                        </span>
                                    </div>
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                            <CheckCircle size={16} className="text-gray-400" />
                                            {t('detail.completionGoal')}
                                        </span>
                                        <span className="font-medium text-gray-900 text-sm text-right">
                                            {completionRequirements
                                                ? `${completionRequirements.requireAllScenarios ? t('detail.allScenarios') : t('detail.scenariosCount', { count: completionRequirements.minScenarios })} ${t('detail.minScore', { score: completionRequirements.minPassingScore })}`
                                                : '—'}
                                        </span>
                                    </div>
                                    {displayData.participationInfo ? (
                                        <div className="pt-2 border-t border-gray-100">
                                            <h4 className="text-sm font-bold text-gray-900 mb-1.5">{t('detail.participationInfo')}</h4>
                                            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                                                {displayData.participationInfo}
                                            </p>
                                        </div>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    {isOpenClass && isOnlineOpenClass && (
                                        <>
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                                    <Globe size={16} className="text-gray-400" />
                                                    {t('openListParticipationMethod')}
                                                </span>
                                                <span className="font-medium text-gray-900 text-sm text-right">
                                                    {t('onlineParticipation')}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                                    <Calendar size={16} className="text-gray-400" />
                                                    {t('detail.participationPeriod')}
                                                </span>
                                                <span className="font-medium text-gray-900 text-sm text-right">
                                                    {onlineOpenClassSidebarPeriodText}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {/* 기관·비오픈 등: 달력 수강기간 */}
                                    {(!isOpenClass || isInPerson) && (
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                                <Calendar size={16} className="text-gray-400" />
                                                {t('detail.participationPeriod')}
                                            </span>
                                            <span className="font-medium text-gray-900 text-sm text-right">
                                                {participationPeriod && participationPeriod.startDate
                                                    ? `${formatDisplayDate(participationPeriod.startDate, i18n.language)} ~ ${formatDisplayDate(participationPeriod.endDate, i18n.language)}`
                                                    : '—'}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="text-gray-500 text-sm flex items-center gap-2 shrink-0">
                                            <CheckCircle size={16} className="text-gray-400" />
                                            {t('detail.completionGoal')}
                                        </span>
                                        <span className="font-medium text-gray-900 text-sm text-right">
                                            {completionRequirements
                                                ? `${completionRequirements.requireAllScenarios ? t('detail.allScenarios') : t('detail.scenariosCount', { count: completionRequirements.minScenarios })} ${t('detail.minScore', { score: completionRequirements.minPassingScore })}`
                                                : '—'}
                                        </span>
                                    </div>
                                    {displayData.participationInfo?.trim() ? (
                                        <div className="pt-2 border-t border-gray-100">
                                            <h4 className="text-sm font-bold text-gray-900 mb-1.5">{t('detail.participationInfo')}</h4>
                                            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                                                {displayData.participationInfo}
                                            </p>
                                        </div>
                                    ) : null}
                                </>
                            )}

                            {!isEnrolled && isOpenClass && (
                                <div className="flex flex-col gap-1.5 pt-3 border-t border-gray-100">
                                    <div className="flex items-center justify-end gap-2">
                                        <span className="text-right">
                                            {displayData.discountPrice !== undefined ? (
                                                <>
                                                    {displayData.price !== undefined && (
                                                        <span className="font-medium text-sm text-gray-500 line-through mr-2">
                                                            {formatCurrencyForLocale(displayData.price, i18n.language)}
                                                        </span>
                                                    )}
                                                    <span className="font-semibold text-base text-blue-600">
                                                        {formatCurrencyForLocale(displayData.discountPrice, i18n.language)}
                                                    </span>
                                                </>
                                            ) : displayData.price !== undefined ? (
                                                <span className="font-semibold text-base text-gray-900">{formatCurrencyForLocale(displayData.price, i18n.language)}</span>
                            ) : (
                                '—'
                            )}
                        </span>
                    </div>
                    {displayData.discountTitle && (
                        <div className="text-xs text-red-500 text-right font-medium">
                            {displayData.discountTitle}
                        </div>
                    )}
                                </div>
                            )}
                        </div>

                        <div className="pt-2 flex flex-col gap-2">
                            {/* 
                                버튼 표시 정책:
                                1. 어드민(role5) → 모든 클래스에서 세션관리 + 초대하기
                                2. 기관관리자(role2) + 본인 기관의 모든 클래스 → 세션관리 + 초대하기
                                3. 기관관리자(role2) + 다른 기관 클래스(오픈클래스) → 학생처럼 (온라인주문+장바구니 or 세션참여)
                                4. 학생 + 오픈클래스 → 온라인주문+장바구니 or 세션참여
                                5. 학생 + 기관클래스 + 속해있음 → 세션참여
                                6. 학생 + 기관클래스 + 속해있지 않음 → 온라인주문+장바구니 (현재 동작)
                            */}
                            {(() => {
                                // 어드민: 모든 클래스에서 세션관리+초대
                                // 기관관리자: 본인 기관의 모든 클래스에서 세션관리+초대
                                if (role === 'admin' || 
                                    (role === 'master' && isOwnOrganizationClass)) {
                                    return (
                                        <div className="flex gap-2">
                                            <Button
                                                variant="primary"
                                                size="md"
                                                className="flex-1"
                                                onClick={() => navigate(`${basePath}/class-management/${classId}/session-management?tab=curriculum`)}
                                            >
                                                {t('detail.sessionManage')}
                                            </Button>
                                            {!isExpired && (
                                                <Button
                                                    variant="outline"
                                                    size="md"
                                                    className="flex-1"
                                                    onClick={() => setIsInviteModalOpen(true)}
                                                >
                                                    {t('detail.invite')}
                                                </Button>
                                            )}
                                        </div>
                                    );
                                }
                                
                                // 기관관리자 + 다른 기관 오픈클래스 or 학생: 수강 여부에 따라
                                if (showSessionJoinCta) {
                                    // 수강 중: 세션 참여 → session-management로 이동
                                    return (
                                        <Button
                                            variant="primary"
                                            size="md"
                                            className="w-full"
                                            onClick={() => {
                                                const basePath = role === 'master' ? '/master/class-management' : '/student/my-classes';
                                                navigate(`${basePath}/${classId}/session-management?tab=curriculum`);
                                            }}
                                            disabled={isExpired}
                                        >
                                            {t('detail.sessionJoin')}
                                        </Button>
                                    );
                                } else {
                                    // 미수강
                                    
                                    // 비오픈클래스(기관 클래스): 세션 참여 버튼 (학생/기관관리자 공통)
                                    if (!isOpenClass) {
                                        return (
                                            <Button
                                                variant="primary"
                                                size="md"
                                                className="w-full"
                                                onClick={() => {
                                                    const basePath = role === 'master' ? '/master/class-management' : '/student/my-classes';
                                                    navigate(`${basePath}/${classId}/session-management?tab=curriculum`);
                                                }}
                                                disabled={isExpired}
                                            >
                                                {t('detail.sessionJoin')}
                                            </Button>
                                        );
                                    }
                                    
                                    // 오픈클래스
                                    // 기관관리자: 세션 참여 버튼
                                    if (role === 'master') {
                                        return (
                                            <Button
                                                variant="primary"
                                                size="md"
                                                className="w-full"
                                                onClick={() => {
                                                    navigate(`/master/class-management/${classId}/session-management?tab=curriculum`);
                                                }}
                                                disabled={isExpired}
                                            >
                                                {t('detail.sessionJoin')}
                                            </Button>
                                        );
                                    }
                                    
                                    // 오픈클래스 + 학생: 온라인 주문 + 장바구니
                                    return (
                                        <div className="flex gap-2">
                                            <Button
                                                variant="primary"
                                                size="md"
                                                className="flex-1"
                                                onClick={() => {
                                                    if (role === 'guest') {
                                                        navigate(ROUTES.AUTH.EMAIL_VERIFICATION);
                                                        return;
                                                    }
                                                    const finalPrice =
                                                        classData.discountPrice !== undefined
                                                            ? classData.discountPrice
                                                            : (classData.price || 0);
                                                    navigate('/order-confirm', {
                                                        state: { selectedClassIds: [classId], totalPrice: finalPrice },
                                                    });
                                                }}
                                                disabled={isExpired}
                                            >
                                                {t('detail.onlineOrder')}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="md"
                                                className="flex-1"
                                                onClick={() => {
                                                    if (addClassToCartIfNew(classId)) {
                                                        alert(t('detail.cartAdded'));
                                                    } else {
                                                        alert(t('detail.cartAlreadyAdded'));
                                                    }
                                                }}
                                            >
                                                <ShoppingCart size={16} className="mr-2" />
                                                {t('detail.addToCart')}
                                            </Button>
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    </div>

                    {showStatusAndParticipants && (
                        <div className="bg-white rounded-xl border border-blue-400 p-6 shadow-sm space-y-6">
                            <h3 className="font-semibold text-gray-900 text-base">{t('detail.classStatus')}</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-500">{t('detail.participantCount')}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900 text-sm">
                                            {isOnlineOpenClass ? (
                                                <span>{currentParticipants}{t('detail.peopleUnit')}</span>
                                            ) : (
                                                <>
                                                    <span className={isFull ? 'text-red-600' : ''}>{currentParticipants}</span>
                                                    <span className="text-gray-400 mx-1">/</span>
                                                    {maxParticipants}
                                                    {t('detail.peopleUnit')}
                                                </>
                                            )}
                                        </span>
                                        {showInviteButton && (role === 'admin' || role === 'master') && (
                                            <button
                                                type="button"
                                                className="flex items-center justify-center w-6 h-6 rounded-md bg-white border border-blue-500 text-blue-500 hover:bg-blue-50 transition-colors"
                                                onClick={() => setIsAddParticipantModalOpen(true)}
                                                aria-label={t('detail.addParticipant')}
                                            >
                                                <span className="text-sm font-semibold">+</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {!isOnlineOpenClass && (
                                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${isFull ? 'bg-red-500' : 'bg-blue-500'}`}
                                            style={{
                                                width: `${Math.min((currentParticipants / Math.max(maxParticipants, 1)) * 100, 100)}%`
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                                {showClassEditActions && (
                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        size="md"
                                        className="flex-1 text-gray-600"
                                        onClick={() => navigate(`${basePath}/class/edit/${classData.id}`)}
                                    >
                                        <Edit size={14} className="mr-1.5" />
                                        {t('detail.classEdit')}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="md"
                                        className="flex-1 text-gray-600"
                                        onClick={() => {
                                            const q = fromClassManagement ? '?from=class-management' : '';
                                            const dupBase =
                                                role === 'admin' && isOpenClass
                                                    ? `${basePath}/open-class/duplicate`
                                                    : `${basePath}/class/duplicate`;
                                            navigate(`${dupBase}/${classData.id}${q}`);
                                        }}
                                    >
                                        <Copy size={14} className="mr-1.5" />
                                        {t('detail.classDuplicate')}
                                    </Button>
                                </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ClassParticipantModal
                isOpen={isParticipantModalOpen}
                onClose={() => setIsParticipantModalOpen(false)}
                classData={classData}
                currentUserOrgId="ORG001"
            />

            {classData && (
                <Modal
                    isOpen={isInviteModalOpen}
                    onClose={() => setIsInviteModalOpen(false)}
                    title={t('detail.inviteTitle')}
                    size="large"
                >
                    <ClassInviteModal classData={classData} />
                </Modal>
            )}

            {classData && (
                <Modal
                    isOpen={isAddParticipantModalOpen}
                    onClose={() => setIsAddParticipantModalOpen(false)}
                    title={t('detail.addParticipantTitle', '참여자 추가')}
                    size="large"
                >
                    <AddParticipantModal
                        classId={classData.id}
                        onSuccess={() => {
                            setIsAddParticipantModalOpen(false);
                            // 클래스 상세와 참가자 목록 쿼리 새로고침
                            queryClient.invalidateQueries({ queryKey: classKeys.detail(Number(classData.id)) });
                            queryClient.invalidateQueries({ queryKey: [...classKeys.users(), Number(classData.id)], exact: false });
                        }}
                    />
                </Modal>
            )}
            
            {selectedCertificateData && (
                <CertificatePreviewModal
                    isOpen={isCertificateModalOpen}
                    onClose={() => {
                        setIsCertificateModalOpen(false);
                        setSelectedCertificateData(null);
                    }}
                    certificateData={selectedCertificateData}
                />
            )}
        </div>
    );
}
